import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  ArrowLeft,
  Users,
  Settings,
  Wrench,
  Mail,
  Activity,
  LayoutDashboard,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useLeagueConfig, type LeagueConfig } from '@/hooks/useLeagueConfig'
import { formatAuditAction } from '@/lib/audit-format'
import { LeagueTools } from '@/components/admin/LeagueTools'
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday'
import { useLeagueMemberCount } from '@/hooks/useLeagueMemberCount'
import { EconomySetup } from '@/components/economy'
import { PaymentTracker } from '@/components/economy/PaymentTracker'
import { toast } from '@/hooks/use-toast'

type Tab = 'overview' | 'members' | 'settings' | 'tools' | 'emails' | 'activity'

const TABS: { value: Tab; label: string; icon: typeof LayoutDashboard }[] = [
  { value: 'overview', label: 'Overview', icon: LayoutDashboard },
  { value: 'members', label: 'Members', icon: Users },
  { value: 'settings', label: 'Settings', icon: Settings },
  { value: 'tools', label: 'Tools', icon: Wrench },
  { value: 'emails', label: 'Emails', icon: Mail },
  { value: 'activity', label: 'Activity', icon: Activity },
]

export default function AdminLeagueDetail() {
  const { leagueId, tab } = useParams<{ leagueId: string; tab?: string }>()
  const navigate = useNavigate()
  const activeTab: Tab = TABS.some(t => t.value === tab) ? (tab as Tab) : 'overview'

  const { data: league, isLoading: isLoadingLeague } = useQuery({
    queryKey: ['admin-league', leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .eq('id', leagueId!)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!leagueId,
  })

  const { config: leagueConfig, competition, competitionId, isLoading: isLoadingConfig } = useLeagueConfig(leagueId)
  const season = league?.season ?? ''
  const { data: currentMatchday } = useCurrentMatchday(season, competitionId)
  const { count: memberCount } = useLeagueMemberCount(leagueId)

  const { data: owner } = useQuery({
    queryKey: ['admin-league-owner', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('display_name, user_id')
        .eq('league_id', leagueId!)
        .eq('role', 'owner')
        .single()
      return data
    },
    enabled: !!leagueId,
  })

  if (isLoadingLeague || isLoadingConfig) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  if (!league) {
    return (
      <div className="p-6">
        <p className="text-zinc-500">League not found</p>
        <Link to="/admin/leagues" className="text-sm text-emerald-600 hover:underline mt-2 inline-block">Back to leagues</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/leagues')}
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 mb-2 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Leagues
        </button>
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">{league.name}</h1>
        <p className="text-sm text-zinc-500 mt-0.5">
          {competition?.name ?? 'Unknown'} · {season} · Matchday {currentMatchday ?? '—'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-200 dark:border-zinc-800">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => navigate(`/admin/leagues/${leagueId}${t.value === 'overview' ? '' : `/${t.value}`}`, { replace: true })}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.value
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab league={league} owner={owner} memberCount={memberCount} competition={competition} />
      )}
      {activeTab === 'members' && leagueId && (
        <MembersTab leagueId={leagueId} />
      )}
      {activeTab === 'settings' && leagueId && league && (
        <SettingsTab leagueId={leagueId} league={league} leagueConfig={leagueConfig} memberCount={memberCount} />
      )}
      {activeTab === 'tools' && leagueId && competitionId && (
        <LeagueTools
          leagueId={leagueId}
          season={season}
          competitionId={competitionId}
          totalMatchdays={leagueConfig?.total_matchdays ?? 38}
          leagueName={league?.name as string ?? ''}
        />
      )}
      {activeTab === 'emails' && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
          <p className="text-sm text-zinc-500">
            Email templates are managed globally.{' '}
            <Link to="/admin/emails" className="text-emerald-600 hover:underline">Go to Email Templates</Link>
          </p>
        </div>
      )}
      {activeTab === 'activity' && leagueId && (
        <ActivityTab leagueId={leagueId} />
      )}
    </div>
  )
}

// ── Overview Tab ──────────────────────────────────────────────────────

function OverviewTab({
  league,
  owner,
  memberCount,
  competition,
}: {
  league: Record<string, unknown>
  owner: { display_name: string; user_id: string } | null | undefined
  memberCount: number
  competition: { name: string; flag?: string } | null | undefined
}) {
  const settings = league.settings as Record<string, unknown> | null
  return (
    <div className="grid grid-cols-2 gap-4">
      <InfoCard label="League Name" value={league.name as string} />
      <InfoCard label="Slug" value={league.slug as string} />
      <InfoCard label="Competition" value={competition?.name ?? 'Unknown'} />
      <InfoCard label="Season" value={league.season as string} />
      <InfoCard label="Members" value={String(memberCount)} />
      <InfoCard label="Creator" value={owner?.display_name ?? '—'} />
      <InfoCard label="Created" value={new Date(league.created_at as string).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
      <InfoCard label="Payment Status" value={(settings?.payment_status as string) ?? 'waived'} />
      <InfoCard label="Max Members" value={String((settings?.maxMembers as number) ?? 20)} />
      {league.description && (
        <div className="col-span-2">
          <InfoCard label="Description" value={league.description as string} />
        </div>
      )}
    </div>
  )
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3">
      <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-sm text-zinc-900 dark:text-zinc-100 mt-1">{value}</p>
    </div>
  )
}

// ── Members Tab ──────────────────────────────────────────────────────

function MembersTab({ leagueId }: { leagueId: string }) {
  const queryClient = useQueryClient()
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const { data: members = [], isLoading, error: membersError } = useQuery({
    queryKey: ['admin-league-members', leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('league_memberships')
        .select('id, user_id, display_name, role, status, payment_status, joined_at')
        .eq('league_id', leagueId)
        .order('display_name')
      if (error) throw error
      return data ?? []
    },
  })

  async function updatePaymentStatus(membershipId: string, status: string) {
    setUpdatingId(membershipId)
    const updateData: Record<string, unknown> = { payment_status: status }
    if (status === 'paid') updateData.paid_at = new Date().toISOString()

    const { error } = await supabase
      .from('league_memberships')
      .update(updateData)
      .eq('id', membershipId)

    if (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' })
    } else {
      toast({ title: 'Payment status updated' })
      queryClient.invalidateQueries({ queryKey: ['admin-league-members', leagueId] })
    }
    setUpdatingId(null)
  }

  if (isLoading) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
  }

  if (membersError) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-5">
        <p className="text-sm text-red-700 dark:text-red-400">Failed to load members: {(membersError as Error).message}</p>
      </div>
    )
  }

  if (members.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 text-center">
        <p className="text-sm text-zinc-400">No members found for this league</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100 dark:border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
            <th className="px-4 py-3 text-left font-medium">Name</th>
            <th className="px-4 py-3 text-left font-medium">Role</th>
            <th className="px-4 py-3 text-left font-medium">Status</th>
            <th className="px-4 py-3 text-left font-medium">Payment</th>
            <th className="px-4 py-3 text-left font-medium">Joined</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {members.map((m) => (
            <tr key={m.id}>
              <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">{m.display_name}</td>
              <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 capitalize">{m.role}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                  m.status === 'active'
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'
                }`}>
                  {m.status}
                </span>
              </td>
              <td className="px-4 py-3">
                {updatingId === m.id ? (
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                ) : (
                  <select
                    value={m.payment_status || 'pending'}
                    onChange={(e) => updatePaymentStatus(m.id, e.target.value)}
                    className="text-xs px-2 py-1 rounded border border-zinc-200 dark:border-zinc-700
                               bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="waived">Waived</option>
                  </select>
                )}
              </td>
              <td className="px-4 py-3 text-zinc-500 text-xs">
                {m.joined_at ? new Date(m.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Settings Tab ──────────────────────────────────────────────────────

function SettingsTab({
  leagueId,
  league,
  leagueConfig,
  memberCount,
}: {
  leagueId: string
  league: Record<string, unknown>
  leagueConfig: LeagueConfig | null
  memberCount: number
}) {
  const queryClient = useQueryClient()
  const settings = league.settings as Record<string, unknown> | null
  const [name, setName] = useState(league.name as string)
  const [description, setDescription] = useState((league.description as string) ?? '')
  const [maxMembers, setMaxMembers] = useState(String((settings?.maxMembers as number) ?? 20))
  const [paymentStatus, setPaymentStatus] = useState((settings?.payment_status as string) ?? 'waived')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    const updatedSettings = { ...settings, maxMembers: parseInt(maxMembers, 10) || 20, payment_status: paymentStatus }
    const { error } = await supabase
      .from('leagues')
      .update({ name, description: description || null, settings: updatedSettings })
      .eq('id', leagueId)

    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' })
    } else {
      toast({ title: 'League settings saved' })
      queryClient.invalidateQueries({ queryKey: ['admin-league', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['admin-leagues'] })
    }
    setSaving(false)
  }

  return (
    <div className="space-y-6">
      {/* Editable league settings */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 space-y-4">
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">League Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs text-zinc-500 font-medium">League Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-zinc-500 font-medium">Max Members</label>
            <input
              type="number"
              value={maxMembers}
              onChange={(e) => setMaxMembers(e.target.value)}
              min={1}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-zinc-500 font-medium">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            >
              <option value="waived">Waived</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <label className="text-xs text-zinc-500 font-medium">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
                       bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Read-only config from league_configs */}
      {leagueConfig && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 space-y-3">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Competition Config</h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-zinc-500">Total Matchdays</p>
              <p className="font-medium text-zinc-900 dark:text-zinc-100">{leagueConfig.total_matchdays ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">TDD Limit</p>
              <p className="font-medium text-zinc-900 dark:text-zinc-100">{leagueConfig.tdd_limit ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Team Pick Limit</p>
              <p className="font-medium text-zinc-900 dark:text-zinc-100">{leagueConfig.team_pick_limit ?? '—'}</p>
            </div>
          </div>
        </div>
      )}

      <EconomySetup leagueId={leagueId} memberCount={memberCount} />
      <PaymentTracker leagueId={leagueId} />
    </div>
  )
}

// ── Activity Tab ──────────────────────────────────────────────────────

function ActivityTab({ leagueId }: { leagueId: string }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['admin-league-activity', leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('id, action, user_id, entity_type, details, created_at')
        .eq('league_id', leagueId)
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return data ?? []
    },
  })

  const { data: members = [] } = useQuery({
    queryKey: ['admin-league-members-names', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('user_id, display_name')
        .eq('league_id', leagueId)
      return data ?? []
    },
  })

  if (isLoading) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
  }

  if (logs.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 text-center">
        <p className="text-sm text-zinc-400">No activity recorded for this league</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 divide-y divide-zinc-100 dark:divide-zinc-800">
      {logs.map((log) => {
        const details = log.details as Record<string, unknown> | null
        const userName = members.find(m => m.user_id === log.user_id)?.display_name
        return (
          <div key={log.id} className="px-5 py-3">
            <p className="text-sm text-zinc-700 dark:text-zinc-300">
              {formatAuditAction(log.action, details, { userName })}
            </p>
            <p className="text-xs text-zinc-400 mt-0.5">
              {new Date(log.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </p>
          </div>
        )
      })}
    </div>
  )
}
